import React, { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Header,
  Icon,
  Pagination,
  Segment,
  StrictPaginationProps,
  Table,
  TableBody,
  TableCell,
  TableHeaderCell,
  TableRow,
} from 'semantic-ui-react'
import { useAppDispatch, useAppSelector } from '../../common/store'
import { APFrameDetailsType } from './ap.types'
import { APUtils } from './ap.util'
import { selectCartItems } from './apCart.slice'
import APFrameDetailsPanel from './APFrameDetailsPanel'
import {
  clearHoverFrame,
  clearSelectFrame,
  selectActiveYear,
  selectAllFrames,
  selectHoverFrame,
  selectSelectedFrame,
  selectVisibleFrames,
  selectYears,
  setActiveYear,
  setHoverFrame,
  setSelectFrame,
  setVisibleFrames,
  setZoomExtentMode,
} from './apFrames.slice'
import {useHistory, useLocation} from "react-router-dom";
import {LAYER_AERIAL_PHOTOGRAPHY_PATH} from "../../environment";


const APDataTable = () => {
  const dispatch = useAppDispatch()
  const allFrames = useAppSelector(selectAllFrames)
  const visibleFrames = useAppSelector(selectVisibleFrames)
  const selectedFrame = useAppSelector(selectSelectedFrame)
  const selectedYear = useAppSelector(selectActiveYear)
  const history = useHistory()
  const location = useRef(useLocation())

  function getUrlSearchParams () {
    return new URLSearchParams(location.current.search)
  }

  // Changes the zoom extent mode for the map when the component is mounted
  useEffect(() => {
    dispatch(setZoomExtentMode('visibleFrames'))
  })

  useEffect( () => {
    let params = getUrlSearchParams()
    if (params.has('frame'))
    {
      let payload = allFrames.find( f => f.frame === Number(params.get("frame")))
      if (payload) {
        dispatch(setSelectFrame(payload))
      }
    }
  }, [])

  useEffect( () => {
    let params = getUrlSearchParams()
    if (params.has("frame")) {
      let payload = allFrames.find( f => f.frame === Number(params.get("frame")))
      if (payload) {
        dispatch(setSelectFrame(payload))
      }
    } else if (params.has("year")) {
      if (params.get("year") !== undefined) {
        let year = Number(params.get("year"))
        // we check this so if we're passed in a year that doesn't exist we default to all years
        if (APUtils.getFramesForYear(allFrames, year).length > 0) {
          dispatch(setActiveYear(year))
        }
      }
    }
  }, [allFrames, visibleFrames])

  useEffect( () => {
    let params = getUrlSearchParams()

    // we need to push the query parameter back into the url here otherwise we'll be showing the table
    // for a selected year but the URL won't match.
    if (selectedFrame?.frame) {
      params.set('film', String(selectedFrame.film_type + '-' + selectedFrame.film))
      params.set('frame', String(selectedFrame.frame))
      params.set('year', String(selectedFrame.year))

      // If we remove the location details from this URL then we'll lose the history of what has been
      // searched for and we'll end up with the frame details on the Data Table emptying.
      history.replace({search: `?${params}`})
    }
  }, [selectedFrame] )


  useEffect( () => {
    let params = getUrlSearchParams()
    if (params.has('year'))
    {
      let year = params.get('year')
      if (selectedYear !== Number(year))
      {
        if (selectedYear !== undefined) {
          // we only want to clear the selected frame if we have a year defined
          dispatch(clearSelectFrame())
        }
      }
    }
  }, [selectedYear])

  /**
   * These key methods below are needed so the initial state of the useState hooks in the components changes when the data changes.
   * When the keys mismatch, it forces the component to be remounted.
   */
  const yearsKey = useMemo(() => {
    return 'years' + allFrames.map(value => value.id).join('')
  }, [allFrames])

  const dataTableKey = useMemo(() => {
    return 'dataTable' + visibleFrames.map(value => value.id).join('')
  }, [visibleFrames])

  return (
    <>
      <Segment attached="top">
        <Header as="h3" textAlign={'center'}>
          Available Imagery
        </Header>
      </Segment>
      <Segment attached>
        {allFrames.length > 0 && <YearsSelector frames={allFrames} key={yearsKey}/>
        }
      </Segment>
      <Segment attached={selectedFrame ? true : 'bottom'}
               style={{
                 display: 'flex',
                 flexDirection: 'column',
                 flexGrow: 1,
                 flexShrink: 1,
                 justifyContent: 'spaceBetween',
                 overflow: 'hidden',
               }}>
        {/*FIXME-Luke loading spinner doesn't show*/}
        {!allFrames && <Segment attached className={'detailsSegment'}>
            <Icon loading name="spinner" size="big"/>
        </Segment>
        }
        {allFrames.length > 0 &&
          visibleFrames && <DataTable columns={['Frame #', 'Project', 'Year']} frames={visibleFrames}
                                      key={dataTableKey}/>
        }
      </Segment>
      {selectedFrame && <Segment attached="bottom">
          <APFrameDetailsPanel selectedFrameDetails={selectedFrame} key={selectedFrame.id}/>
      </Segment>}
    </>
  )
}

type DataTableProps = {
  columns: string[]
  frames: APFrameDetailsType[]
}

const DataTable = ({ columns, frames }: DataTableProps) => {
  const pageSize = 10
  const totalPages = Math.ceil(frames.length / pageSize)

  const [activePage, setActivePage] = useState(1)

  const selectedFrame = useAppSelector(selectSelectedFrame)
  const selectedYear = useAppSelector(selectActiveYear)

  useEffect(() => {
    // the purpose of this useEffect is to run when the selected frame is changed via the URL
    // a side-effect is that it will also run when the change page event is fired
    if (selectedFrame && selectedFrame.id) {
      // We only want to do this if the year matches what is in the frame, otherwise we'll let the default
      // action happen in the handleChangePage event
      if (selectedFrame.year === selectedYear) {
        let index = frames.findIndex((f) => f.id === selectedFrame.id) + 1
        let newActivePage = Math.ceil(index / pageSize)
        setActivePage(newActivePage)
      }
    }
  }, [selectedFrame]);

  const framesForPage = useMemo(() => {
    return frames.slice((activePage - 1) * pageSize, (activePage) * pageSize)
  }, [frames, activePage])

  const handleChangePage = (event: SyntheticEvent, { activePage }: StrictPaginationProps) => {
    setActivePage(Number(activePage))
  }

  return (
    <>
      <Table className={'apDataTable'} size="small" singleLine collapsing={false} unstackable striped compact>
        <TableHeader columns={columns}/>
        <TableBody className={'apDataTableBody'}>
          {framesForPage.map((row) => <DataTableRow row={row} key={row.id}/>)}
        </TableBody>
      </Table>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Pagination activePage={activePage} totalPages={totalPages} onPageChange={handleChangePage}/>
      </div>
    </>
  )
}

type TableHeaderProps = {
  columns: string[]
}

const TableHeader = ({ columns }: TableHeaderProps) => {
  return (
    <Table.Header className={'header'}>
      <Table.Row>
        {
          columns.map((column) => {
            return (
              <TableHeaderCell key={column}>{column}</TableHeaderCell>
            )
          })
        }
      </Table.Row>
    </Table.Header>
  )
}

type DataTableRowProps = {
  row: APFrameDetailsType
}

const DataTableRow = ({ row }: DataTableRowProps) => {
  const dispatch = useAppDispatch()
  const selectedFrame = useAppSelector(selectSelectedFrame)
  const hoverFrame = useAppSelector(selectHoverFrame)

  const cartFrames = useAppSelector(selectCartItems)

  const onClick = () => {
    if (!selectedFrame || selectedFrame.id !== row.id) {
      dispatch(setSelectFrame(row))
    } else {
      dispatch(clearSelectFrame())
    }
  }

  const onMouseOver = (event: any) => {
    dispatch(setHoverFrame(row))
  }

  const onMouseOut = (event: any) => {
    if (hoverFrame && hoverFrame === row) {
      dispatch(clearHoverFrame())
    }
  }

  const isInCart = useMemo(() => {
    return cartFrames.find(value => value.frame.id === row.id) !== undefined
  }, [cartFrames, row.id])

  const cssClasses = useMemo(() => {
    let cssClasses = 'apDataTableRow'
    if (isInCart) {
      cssClasses += ' apDataTableRowInCart'
    }
    if (selectedFrame === row) {
      cssClasses += ' apDataTableRowSelected'
    }
    return cssClasses
  }, [row, selectedFrame, isInCart])

  return (
    <TableRow className={cssClasses}
              textAlign={'left'}
              key={row.id}
              onClick={onClick}
              onMouseOver={onMouseOver}
              onMouseOut={onMouseOut}
    >
      <TableCell> {row.frame} </TableCell>
      <TableCell> {row.project} </TableCell>
      <TableCell> {row.year} </TableCell>
    </TableRow>
  )
}

type YearsSelectorProps = {
  frames: APFrameDetailsType[]
}

const YearsSelector = ({ frames }: YearsSelectorProps) => {
  const dispatch = useAppDispatch()
  const years = useAppSelector(selectYears)
  const activeYear = useAppSelector(selectActiveYear)
  const history = useHistory()

  const isActiveYear = React.useCallback((year: number) => {
    return activeYear === year
  }, [activeYear])

  useEffect( () => {
    if (activeYear) {
      dispatch(setVisibleFrames(APUtils.getFramesForYear(frames, activeYear)))
    }
  }, [activeYear])

  const handleChangeYear = (year: number) => {
    if (year > 0) {
      history.push({search: `?${new URLSearchParams({year: String(year)})}`})
      dispatch(setVisibleFrames(APUtils.getFramesForYear(frames, year)))
    } else {
      history.push(history.location.pathname)
      dispatch(setVisibleFrames(frames))
    }
    dispatch(setActiveYear(year))
  }

  return (
    <span className={'scrollContainer scrollPadding scrollBorder'}>
            <div className={'scroll'}>
              <YearButton
                year={0}
                active={isActiveYear(0)}
                changeYear={() => handleChangeYear(0)}
                key={0}
              />
              {years && years.map((year) =>
                <YearButton
                  year={year}
                  active={isActiveYear(year)}
                  changeYear={() => handleChangeYear(year)}
                  key={year}
                />,
              )}
            </div>
          </span>
  )
}

type YearButtonProps = {
  year: number
  active: boolean
  changeYear: () => void
}

const YearButton = ({ year, active, changeYear }: YearButtonProps) => {

  const yearStyle: Set<String> = new Set<string>().add('mini')
  if (active) {
    yearStyle.add('primary')
  }
  const cssClasses = [...yearStyle].join(' ')
  return (
    <Button className={cssClasses} onClick={changeYear}>
      {year === 0 ? 'All' : year}
    </Button>
  )
}

export default APDataTable